import { ref, readonly } from 'vue'
import { ElNotification } from 'element-plus'

export const ErrorType = {
  ACCESS_DENIED: 'access_denied', // 403, full-screen forbidden
  SERVER_ERROR: 'server_error', // 5xx
  NETWORK_ERROR: 'network_error', // lost connection
  RATE_LIMITED: 'rate_limited', // 429
  NOT_FOUND: 'not_found', // 404, usually handled locally
}

const isForbidden = ref(false)
const currentError = ref(null)

export function triggerAccessDenied() {
  if (isForbidden.value) {
    return false
  }
  
  isForbidden.value = true
  currentError.value = {
    type: ErrorType.ACCESS_DENIED,
    message: 'Access denied',
  }
  return true
}

export function resetForbidden() {
  isForbidden.value = false
  if (currentError.value?.type === ErrorType.ACCESS_DENIED) {
    currentError.value = null
  }
}

// duration 0 means the toast stays until dismissed by hand
export function showErrorNotification({ type, message, title, duration = 5000 }) {
  const notificationTypes = {
    [ErrorType.SERVER_ERROR]: { title: title || 'Server Error', elType: 'error' },
    [ErrorType.NETWORK_ERROR]: { title: title || 'Connection Error', elType: 'warning' },
    [ErrorType.RATE_LIMITED]: { title: title || 'Rate Limited', elType: 'warning' },
    [ErrorType.NOT_FOUND]: { title: title || 'Not Found', elType: 'info' },
  }
  
  const config = notificationTypes[type] || { title: title || 'Error', elType: 'error' }
  
  ElNotification({
    title: config.title,
    message: message,
    type: config.elType,
    duration: duration,
  })
}

export function triggerServerError(message = 'Something went wrong. Please try again later.') {
  showErrorNotification({
    type: ErrorType.SERVER_ERROR,
    message,
    duration: 7000,
  })
}

export function triggerNetworkError(message = 'Connection lost. Please check your internet connection.') {
  showErrorNotification({
    type: ErrorType.NETWORK_ERROR,
    message,
    duration: 0,
  })
}

export function triggerRateLimitError(message = 'Too many requests. Please wait a moment.') {
  showErrorNotification({
    type: ErrorType.RATE_LIMITED,
    message,
    duration: 10000,
  })
}

// Returns true when the error was shown globally, so callers can skip their own toast.
export function handleHttpError(status, data = {}, options = {}) {
  if (options.skipGlobalErrorHandling) {
    return false
  }

  switch (status) {
    case 403:
      if (options.skipAccessDeniedHandling !== true) {
        triggerAccessDenied()
        return true
      }
      break
      
    case 429:
      if (options.skipRateLimitHandling !== true) {
        const message = data.message || 'Too many requests. Please wait a moment.'
        triggerRateLimitError(message)
        return true
      }
      break
      
    case 500:
    case 502:
    case 503:
    case 504:
      if (options.skipServerErrorHandling !== true) {
        const message = data.message || 'Something went wrong. Please try again later.'
        triggerServerError(message)
        return true
      }
      break
      
    default:
      // Any other 4xx: only worth a toast if the server gave us a message.
      if (status >= 400 && status < 500 && options.skipClientErrorHandling !== true) {
        if (data.message) {
          showErrorNotification({
            type: ErrorType.NOT_FOUND,
            title: 'Error',
            message: data.message,
            duration: 5000,
          })
          return true
        }
      }
  }
  
  return false
}

export function handleNetworkError(error, options = {}) {
  if (options.skipGlobalErrorHandling || options.skipNetworkErrorHandling) {
    return false
  }

  if (error instanceof TypeError && error.message.includes('fetch')) {
    triggerNetworkError()
    return true
  }
  
  return false
}

export function useGlobalError() {
  return {
    isForbidden: readonly(isForbidden),
    currentError: readonly(currentError),
    triggerAccessDenied,
    resetForbidden,
    showErrorNotification,
    triggerServerError,
    triggerNetworkError,
    triggerRateLimitError,
    handleHttpError,
    handleNetworkError,
    ErrorType,
  }
}
